import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { 
  ArrowRight, ArrowLeft, Clock, User, Calendar, Share2,
  Heart, MessageSquare, Bookmark, ChevronRight, Loader2,
  TrendingUp, Home, Users, Euro, Building2
} from 'lucide-react';

// Static blog posts for fallback content
const STATIC_POSTS = [
  {
    id: 'huurmarkt-nederland-2026',
    slug: 'huurmarkt-nederland-2026',
    title: 'Huurmarkt Nederland 2026: Wat je moet weten als huurder',
    excerpt: 'De Nederlandse huurmarkt blijft uitdagend. Dit zijn de belangrijkste trends en tips voor woningzoekers.',
    content: `
      <h2>De huidige stand van zaken</h2>
      <p>De Nederlandse huurmarkt blijft krap in 2026. Met een tekort van ruim 300.000 woningen en stijgende prijzen is het vinden van een huurwoning nog steeds een uitdaging. Maar er is hoop: nieuwe digitale platformen zoals Swipe je woning maken het zoekproces efficiënter.</p>
      
      <h2>Trends die je moet kennen</h2>
      <ul>
        <li><strong>Digitalisering:</strong> Steeds meer verhuurders kiezen voor online matching-platformen</li>
        <li><strong>Flexibele huur:</strong> Kortere contracten worden populairder, vooral in de grote steden</li>
        <li><strong>Duurzaamheid:</strong> Energielabels worden belangrijker, zowel voor huurders als verhuurders</li>
        <li><strong>Expat-markt:</strong> Grote vraag van internationale professionals</li>
      </ul>
      
      <h2>Tips voor woningzoekers</h2>
      <p>Om je kansen te vergroten adviseren we het volgende:</p>
      <ol>
        <li>Maak een compleet profiel aan met een persoonlijke beschrijving</li>
        <li>Reageer snel op nieuwe woningen - de eerste 24 uur zijn cruciaal</li>
        <li>Wees flexibel in je zoekgebied - kijk ook net buiten de ring</li>
        <li>Zorg voor alle documenten: werkgeversverklaring, identiteitsbewijs, etc.</li>
      </ol>
      
      <h2>Conclusie</h2>
      <p>Hoewel de huurmarkt uitdagend blijft, zijn er steeds meer tools beschikbaar om het zoekproces te vergemakkelijken. Met de juiste strategie en een beetje geduld vind je jouw droomwoning.</p>
    `,
    author: 'Marieke van den Berg',
    authorRole: 'Woningmarkt Expert',
    category: 'Markt',
    readTime: '6 min',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200',
    publishedAt: '2026-01-28',
    featured: true
  },
  {
    id: 'expats-woning-vinden',
    slug: 'expats-woning-vinden-nederland',
    title: 'Als expat een woning vinden in Nederland: De complete gids',
    excerpt: 'Verhuizen naar Nederland? Dit is alles wat internationale professionals moeten weten over het vinden van een huurwoning.',
    content: `
      <h2>Welkom in Nederland!</h2>
      <p>Nederland trekt jaarlijks duizenden internationale professionals aan. Of je nu komt voor werk bij een multinational, een startup, of als kennismigrant - het vinden van een woning is vaak de eerste grote uitdaging.</p>
      
      <h2>De Nederlandse huurmarkt begrijpen</h2>
      <p>Anders dan in veel andere landen werkt de Nederlandse huurmarkt met:</p>
      <ul>
        <li><strong>Sociale huur:</strong> Gesubsidieerde woningen (meestal niet toegankelijk voor expats)</li>
        <li><strong>Vrije sector:</strong> Marktconforme huurprijzen (€800-€2500+ per maand)</li>
        <li><strong>Antikraak:</strong> Tijdelijke bewoning van leegstaande panden</li>
      </ul>
      
      <h2>Wat verhuurders verwachten</h2>
      <p>Nederlandse verhuurders vragen vaak om:</p>
      <ol>
        <li>Arbeidscontract of werkgeversverklaring</li>
        <li>Bewijs van inkomen (meestal 3x de huur bruto)</li>
        <li>Kopie paspoort/ID</li>
        <li>Bankafschriften (soms)</li>
      </ol>
      
      <h2>Tips van succesvolle expats</h2>
      <blockquote>"Ik heb Swipe je woning gebruikt omdat het veel persoonlijker voelt. Verhuurders kunnen direct zien wie ik ben en wat ik zoek." - James, Software Engineer uit Londen</blockquote>
      
      <h2>Populaire steden voor expats</h2>
      <p>Amsterdam, Rotterdam, Den Haag en Eindhoven zijn het meest populair onder internationale professionals. Elke stad heeft zijn eigen karakter en prijsniveau.</p>
    `,
    author: 'Thomas Bakker',
    authorRole: 'Relocation Specialist',
    category: 'Gids',
    readTime: '8 min',
    image: 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=1200',
    publishedAt: '2026-01-25',
    featured: false
  },
  {
    id: 'verhuurder-tips-succes',
    slug: 'verhuurder-tips-meer-reacties',
    title: '7 tips voor verhuurders: Zo krijg je meer en betere reacties',
    excerpt: 'Als verhuurder wil je snel de perfecte huurder vinden. Deze bewezen tips helpen je om meer en betere reacties te krijgen.',
    content: `
      <h2>De kunst van een goede advertentie</h2>
      <p>Met duizenden woningen online is het belangrijk om op te vallen. Verhuurders die onze tips volgen krijgen gemiddeld 3x meer reacties.</p>
      
      <h2>Tip 1: Investeer in foto's</h2>
      <p>Foto's zijn het eerste wat potentiële huurders zien. Zorg voor:</p>
      <ul>
        <li>Minimaal 8-10 foto's van alle ruimtes</li>
        <li>Goede belichting (overdag fotograferen)</li>
        <li>Opgeruimde kamers</li>
        <li>Horizontale foto's (geen selfie-modus)</li>
      </ul>
      
      <h2>Tip 2: Schrijf een persoonlijke beschrijving</h2>
      <p>Vertel niet alleen wat, maar ook waarom. Wat maakt jouw woning speciaal? Is er een gezellige bakker op de hoek? Een park om te hardlopen?</p>
      
      <h2>Tip 3: Wees eerlijk over de voorwaarden</h2>
      <p>Vermeld duidelijk: huisdieren toegestaan? Inschrijving mogelijk? Dit voorkomt verrassingen later.</p>
      
      <h2>Tip 4: Reageer snel</h2>
      <p>Data laat zien dat verhuurders die binnen 24 uur reageren 47% meer kans hebben op een succesvolle match.</p>
      
      <h2>Tip 5: Kies het juiste platform</h2>
      <p>Platformen zoals Swipe je woning matchen je automatisch met huurders die passen bij jouw voorkeuren.</p>
    `,
    author: 'Lisa de Vries',
    authorRole: 'Content Manager',
    category: 'Tips',
    readTime: '5 min',
    image: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=1200',
    publishedAt: '2026-01-22',
    featured: false
  },
  {
    id: 'amsterdam-wijken-gids',
    slug: 'beste-wijken-amsterdam-huren',
    title: 'De beste wijken om te huren in Amsterdam: Complete gids 2026',
    excerpt: 'Van De Pijp tot Noord: ontdek welke Amsterdamse wijk bij jouw levensstijl en budget past.',
    content: `
      <h2>Amsterdam: Een stad van wijken</h2>
      <p>Amsterdam bestaat uit tientallen unieke wijken, elk met een eigen karakter. Of je nu van het bruisende stadsleven houdt of liever rustig woont - er is voor ieder wat wils.</p>
      
      <h2>De Pijp - Voor de sociale vlinder</h2>
      <p>Bekend om de Albert Cuypmarkt, gezellige terrassen en multiculturele sfeer. Prijzen: €1.200 - €2.000 voor een appartement.</p>
      
      <h2>Jordaan - Klassiek Amsterdam</h2>
      <p>Pittoreske grachten, galerietjes en bruine kroegen. Premium locatie met premium prijzen: €1.500 - €2.500+</p>
      
      <h2>Oost - De nieuwe hotspot</h2>
      <p>Opkomende wijk met hippe restaurants en parken. Goede prijs-kwaliteitverhouding: €1.000 - €1.800</p>
      
      <h2>Noord - De frontier</h2>
      <p>Over het IJ, creatief en betaalbaar. Goed openbaar vervoer naar centrum. €900 - €1.500</p>
      
      <h2>Oud-Zuid - Rustig en chic</h2>
      <p>Vondelpark, musea, ambassades. Ideaal voor gezinnen met hoger budget: €1.500 - €3.000+</p>
      
      <h2>Onze tip</h2>
      <p>Bepaal eerst je prioriteiten: wil je dicht bij werk zijn, in een levendige buurt wonen, of juist rust? Gebruik dan een platform als Swipe je woning om woningen te vinden die precies matchen met jouw wensen.</p>
    `,
    author: 'Marieke van den Berg',
    authorRole: 'Woningmarkt Expert',
    category: 'Gids',
    readTime: '7 min',
    image: 'https://images.unsplash.com/photo-1576924542622-772281b13aa8?w=1200',
    publishedAt: '2026-01-18',
    featured: false
  },
  {
    id: 'eerste-kamer-huren',
    slug: 'eerste-kamer-huren-tips-starters',
    title: 'Je eerste kamer huren: Tips voor starters en studenten',
    excerpt: 'Op kamers gaan? Dit moet je weten voordat je je eerste huurcontract tekent.',
    content: `
      <h2>Het begin van een nieuw avontuur</h2>
      <p>Je eerste eigen kamer huren is een grote stap. Of je nu gaat studeren of je eerste baan hebt - hier zijn de belangrijkste zaken om op te letten.</p>
      
      <h2>Budget bepalen</h2>
      <p>Een vuistregel: besteed maximaal 30-35% van je inkomen aan huur. Als student kun je huurtoeslag aanvragen als je aan de voorwaarden voldoet.</p>
      
      <h2>Waar moet je op letten?</h2>
      <ul>
        <li>Is de kamer gemeubileerd of kaal?</li>
        <li>Wat zijn de bijkomende kosten (gas, water, licht, wifi)?</li>
        <li>Mag je je inschrijven op het adres?</li>
        <li>Hoe zijn de huisgenoten?</li>
        <li>Wat zijn de huisregels?</li>
      </ul>
      
      <h2>Het bezichtigen</h2>
      <p>Neem de tijd tijdens een bezichtiging. Check:</p>
      <ol>
        <li>Staat van de kamer (vocht, schimmel, isolatie)</li>
        <li>Gedeelde ruimtes (keuken, badkamer)</li>
        <li>Sfeer in het huis</li>
        <li>Buurt en voorzieningen</li>
      </ol>
      
      <h2>Succesverhaal</h2>
      <blockquote>"Via Swipe je woning vond ik niet alleen een kamer, maar ook drie geweldige huisgenoten. De persoonlijke profielen maken echt het verschil." - Emma, 22, student in Utrecht</blockquote>
    `,
    author: 'Thomas Bakker',
    authorRole: 'Relocation Specialist',
    category: 'Tips',
    readTime: '6 min',
    image: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=1200',
    publishedAt: '2026-01-15',
    featured: false
  }
];

// Blog list page
export const BlogPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await api.get('/blogs');
        
        if (response.data.posts && response.data.posts.length > 0) {
          setPosts(response.data.posts);
        } else {
          // Use static posts as fallback
          setPosts(STATIC_POSTS);
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
        // Use static posts as fallback
        setPosts(STATIC_POSTS);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const featuredPost = posts.find(p => p.featured);
  const otherPosts = posts.filter(p => !p.featured);

  const categoryColors = {
    'Markt': 'bg-blue-100 text-blue-700',
    'Gids': 'bg-green-100 text-green-700',
    'Tips': 'bg-purple-100 text-purple-700',
    'Verhaal': 'bg-orange-100 text-orange-700',
    'Product': 'bg-pink-100 text-pink-700',
    'Huurders': 'bg-emerald-100 text-emerald-700',
    'Verhuurders': 'bg-indigo-100 text-indigo-700',
    'Steden': 'bg-amber-100 text-amber-700',
    'Juridisch': 'bg-red-100 text-red-700'
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="blog-page">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Clean Hero */}
        <section className="py-16 md:py-20 px-4 bg-[#F7F7F7] border-b border-[#E5E5E5]">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#2E7D32]/10 text-[#2E7D32] text-sm font-semibold mb-6">
              Blog & Nieuws
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-[#2A2A2A] mb-4">
              Alles over huren in Nederland
            </h1>
            <p className="text-lg text-[#666] max-w-2xl mx-auto">
              Tips, gidsen en het laatste nieuws over de huurmarkt voor huurders en verhuurders
            </p>
          </div>
        </section>

        {/* Loading State */}
        {loading && (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#2E7D32]" />
          </div>
        )}

        {/* Featured Post */}
        {!loading && featuredPost && (
          <section className="py-12 px-4">
            <div className="max-w-6xl mx-auto">
              <Link 
                to={`/blog/${featuredPost.slug}`}
                className="block bg-[#F7F7F7] rounded-2xl overflow-hidden hover:shadow-xl transition-all group"
              >
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="h-64 md:h-80">
                    <img 
                      src={featuredPost.image} 
                      alt={featuredPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 md:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${categoryColors[featuredPost.category] || 'bg-gray-100 text-gray-700'}`}>
                        {featuredPost.category}
                      </span>
                      <span className="text-sm text-[#9E9E9E] flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {featuredPost.readTime}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-4 group-hover:text-[#2E7D32] transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="text-[#666] mb-6 line-clamp-2">{featuredPost.excerpt}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#2E7D32] rounded-full flex items-center justify-center text-white font-bold">
                        {featuredPost.author[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-[#2A2A2A] text-sm">{featuredPost.author}</p>
                        <p className="text-xs text-[#9E9E9E]">{featuredPost.publishedAt}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* Other Posts Grid */}
        <section className="py-12 px-4 bg-[#F7F7F7]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-[#2A2A2A] mb-8">Alle artikelen</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                >
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${categoryColors[post.category] || 'bg-gray-100 text-gray-700'}`}>
                        {post.category}
                      </span>
                      <span className="text-xs text-[#9E9E9E] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {post.readTime}
                      </span>
                    </div>
                    <h3 className="font-bold text-[#2A2A2A] mb-2 group-hover:text-[#2E7D32] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-[#666] line-clamp-2 mb-4">{post.excerpt}</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#2E7D32] rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {post.author[0]}
                      </div>
                      <span className="text-xs text-[#9E9E9E]">{post.publishedAt}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-4">
              Klaar om je droomwoning te vinden?
            </h2>
            <p className="text-[#9E9E9E] mb-6">
              Duizenden woningzoekers en verhuurders vonden elkaar al via Swipe je woning
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/swipematch')}
                className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-12 px-8 rounded-full font-bold"
              >
                Start met zoeken
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/verhuurder/onboarding')}
                variant="outline"
                className="border-[#1F3A5F] text-[#1F3A5F] h-12 px-8 rounded-full font-bold"
              >
                Plaats een woning
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Individual blog post page
export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        // Try to fetch from API
        const response = await api.get(`/blogs/${slug}`);
        setPost(response.data);
        
        // Fetch related posts
        const relatedResponse = await api.get('/blogs?limit=4');
        const filtered = relatedResponse.data.posts.filter(p => p.slug !== slug).slice(0, 3);
        setRelatedPosts(filtered.length > 0 ? filtered : STATIC_POSTS.filter(p => p.slug !== slug).slice(0, 3));
      } catch (err) {
        console.error('Error fetching blog:', err);
        // Try static posts as fallback
        const staticPost = STATIC_POSTS.find(p => p.slug === slug);
        if (staticPost) {
          setPost(staticPost);
          setRelatedPosts(STATIC_POSTS.filter(p => p.slug !== slug).slice(0, 3));
        } else {
          setError('Blog niet gevonden');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  const categoryColors = {
    'Markt': 'bg-blue-100 text-blue-700',
    'Gids': 'bg-green-100 text-green-700',
    'Tips': 'bg-purple-100 text-purple-700',
    'Verhaal': 'bg-orange-100 text-orange-700',
    'Product': 'bg-pink-100 text-pink-700',
    'Huurders': 'bg-emerald-100 text-emerald-700',
    'Verhuurders': 'bg-indigo-100 text-indigo-700',
    'Steden': 'bg-amber-100 text-amber-700',
    'Juridisch': 'bg-red-100 text-red-700'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
        <Header />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2E7D32]" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
        <Header />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-[#2A2A2A] mb-4">Artikel niet gevonden</h1>
            <Button onClick={() => navigate('/blog')} className="bg-[#2E7D32] hover:bg-[#256B2B] text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Terug naar blog
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="blog-post-page">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Clean Hero */}
        <div className="bg-[#F7F7F7] border-b border-[#E5E5E5]">
          <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
            <button 
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-[#9E9E9E] hover:text-[#2E7D32] mb-6 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Terug naar blog
            </button>
            
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${categoryColors[post.category] || 'bg-gray-100 text-gray-700'} mb-4`}>
              {post.category}
            </span>
            
            <h1 className="text-3xl md:text-5xl font-black text-[#2A2A2A] leading-tight mb-6">
              {post.title}
            </h1>
            
            <p className="text-lg text-[#666] mb-8 max-w-2xl">
              {post.excerpt}
            </p>
            
            {/* Author & Meta - Clean inline */}
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#2E7D32] rounded-full flex items-center justify-center text-white font-bold">
                  {post.author[0]}
                </div>
                <div>
                  <p className="font-semibold text-[#2A2A2A]">{post.author}</p>
                  <p className="text-[#9E9E9E]">{post.authorRole}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-[#9E9E9E]">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> {post.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {post.readTime}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="max-w-5xl mx-auto px-4 -mt-2">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>
        </div>

        {/* Article Content */}
        <article className="py-12 md:py-16 px-4">
          <div className="max-w-3xl mx-auto">
            {/* Content */}
            <div 
              className="prose prose-lg max-w-none 
                prose-headings:font-bold prose-headings:text-[#2A2A2A] prose-headings:mt-10 prose-headings:mb-4
                prose-h2:text-2xl prose-h3:text-xl
                prose-p:text-[#444] prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-[#2E7D32] prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                prose-strong:text-[#2A2A2A] prose-strong:font-semibold
                prose-li:text-[#444] prose-li:leading-relaxed
                prose-ul:my-6 prose-ol:my-6
                prose-blockquote:border-l-4 prose-blockquote:border-[#2E7D32] prose-blockquote:bg-[#F7F7F7] 
                prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic
                prose-blockquote:text-[#444] prose-blockquote:font-medium"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Share & Actions */}
            <div className="flex items-center justify-between mt-16 pt-8 border-t border-[#E5E5E5]">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7F7F7] text-[#666] hover:bg-[#2E7D32] hover:text-white transition-all">
                  <Heart className="w-4 h-4" /> Leuk
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7F7F7] text-[#666] hover:bg-[#2E7D32] hover:text-white transition-all">
                  <Bookmark className="w-4 h-4" /> Opslaan
                </button>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7F7F7] text-[#666] hover:bg-[#1F3A5F] hover:text-white transition-all">
                <Share2 className="w-4 h-4" /> Delen
              </button>
            </div>

            {/* CTA Box */}
            <div className="bg-gradient-to-r from-[#1F3A5F] to-[#2E7D32] rounded-2xl p-6 md:p-8 mt-12 text-white">
              <h3 className="text-xl font-bold mb-2">Klaar om te beginnen?</h3>
              <p className="text-white/80 mb-4">Vind je droomwoning of perfecte huurder via Swipe je woning</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => navigate('/swipematch')}
                  className="bg-white text-[#1F3A5F] hover:bg-white/90 h-11 px-6 rounded-full font-bold"
                >
                  Zoek een woning
                </Button>
                <Button 
                  onClick={() => navigate('/verhuurder/onboarding')}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 h-11 px-6 rounded-full font-bold"
                >
                  Verhuur je woning
                </Button>
              </div>
            </div>
          </div>
        </article>

        {/* Related Posts */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-[#2A2A2A] mb-8">Gerelateerde artikelen</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="bg-[#F7F7F7] rounded-xl overflow-hidden hover:shadow-lg transition-all group"
                >
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${categoryColors[relatedPost.category] || 'bg-gray-100 text-gray-700'} mb-2`}>
                      {relatedPost.category}
                    </span>
                    <h3 className="font-bold text-[#2A2A2A] group-hover:text-[#2E7D32] transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
